import React, { useState } from 'react';
import { useApiSettings } from '../hooks/useApiSettings';
import StatusManagement from './StatusManagement';
import UserManagement from './UserManagement';
import AircraftTypeManagement from './AircraftTypeManagement';
import CarrierManagement from './CarrierManagement';
import CarManagement from './CarManagement';
import FlightManagement from './FlightManagement';
import CalibrationTableManagement from './CalibrationTableManagement';
import AirportManagement from './AirportManagement';
import './SettingsPage.css';

const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'api' | 'general' | 'statuses' | 'users' | 'aircraft-types' | 'carriers' | 'cars' | 'flights' | 'calibration-tables' | 'airports' | 'about'>('api');
  const { settings, saveSettings, resetSettings, testConnection } = useApiSettings();

  const handleSettingChange = (field: keyof typeof settings, value: string | number | boolean) => {
    saveSettings({ [field]: value });
  };

  const handleReset = () => {
    resetSettings();
  };

  const handleTestConnection = async () => {
    const isConnected = await testConnection();
    if (isConnected) {
      alert('Соединение с API успешно установлено!');
    } else {
      alert('Ошибка соединения с API. Проверьте настройки.');
    }
  };

  const tabs = [
    { id: 'api', label: 'API Настройки', icon: '' },
    { id: 'general', label: 'Общие', icon: '' },
    { id: 'statuses', label: 'Статусы', icon: '' },
    { id: 'users', label: 'Пользователи', icon: '' },
    { id: 'aircraft-types', label: 'Типы самолетов', icon: '' },
    { id: 'carriers', label: 'Перевозчики', icon: '' },
    { id: 'cars', label: 'Машины', icon: '' },
    { id: 'flights', label: 'Рейсы', icon: '' },
    { id: 'calibration-tables', label: 'Тарировочные таблицы', icon: '' },
    { id: 'airports', label: 'Аэропорты', icon: '' },
    { id: 'about', label: 'О системе', icon: '' }
  ];

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1 className="page-title">Настройки</h1>
      </div>

      <div className="settings-container">
        <div className="settings-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={`settings-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id as any)}
            >
              {tab.icon && <span className="tab-icon">{tab.icon}</span>}
              <span className="tab-label">{tab.label}</span>
            </button>
          ))}
        </div>

        <div className="settings-content">
          {activeTab === 'api' && (
            <div className="settings-section">
              <h3>Настройки API</h3>
              
              <div className="setting-group">
                <label htmlFor="baseUrl">Базовый URL API:</label>
                <input
                  id="baseUrl"
                  type="text"
                  value={settings.baseUrl}
                  onChange={(e) => handleSettingChange('baseUrl', e.target.value)}
                  placeholder="http://localhost:8081/api"
                />
                <small>Базовый URL для всех API запросов</small>
              </div>

              <div className="setting-group">
                <label htmlFor="timeout">Таймаут запросов (мс):</label>
                <input
                  id="timeout"
                  type="number"
                  value={settings.timeout}
                  onChange={(e) => handleSettingChange('timeout', parseInt(e.target.value))}
                  min="1000"
                  max="120000"
                />
                <small>Максимальное время ожидания ответа от сервера</small>
              </div>

              <div className="setting-group">
                <label htmlFor="retryAttempts">Количество попыток:</label>
                <input
                  id="retryAttempts"
                  type="number"
                  value={settings.retryAttempts}
                  onChange={(e) => handleSettingChange('retryAttempts', parseInt(e.target.value))}
                  min="1"
                  max="10"
                />
                <small>Количество повторных попыток при ошибке</small>
              </div>

              <div className="setting-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={settings.enableLogging}
                    onChange={(e) => handleSettingChange('enableLogging', e.target.checked)}
                  />
                  Включить логирование API запросов
                </label>
                <small>Записывать все API запросы в консоль браузера</small>
              </div>

              <div className="settings-actions">
                <button onClick={handleTestConnection} className="test-connection-btn">
                  Проверить соединение
                </button>
                <button onClick={handleReset} className="reset-btn">
                  Сбросить настройки
                </button>
              </div>
            </div>
          )}

          {activeTab === 'general' && (
            <div className="settings-section">
              <h3>Общие настройки</h3>
              
              <div className="setting-group">
                <label className="checkbox-label">
                  <input type="checkbox" defaultChecked />
                  Автоматическое обновление данных
                </label>
                <small>Автоматически обновлять данные каждые 30 секунд</small>
              </div>

              <div className="setting-group">
                <label className="checkbox-label">
                  <input type="checkbox" defaultChecked />
                  Уведомления о новых заявках
                </label>
                <small>Показывать уведомления при появлении новых заявок</small>
              </div>

              <div className="setting-group">
                <label htmlFor="language">Язык интерфейса:</label>
                <select id="language" defaultValue="ru">
                  <option value="ru">Русский</option>
                  <option value="en">English</option>
                  <option value="kk">Қазақша</option>
                </select>
              </div>

              <div className="setting-group">
                <label htmlFor="theme">Тема оформления:</label>
                <select id="theme" defaultValue="auto">
                  <option value="auto">Автоматически</option>
                  <option value="light">Светлая</option>
                  <option value="dark">Темная</option>
                </select>
              </div>
            </div>
          )}

          {activeTab === 'statuses' && (
            <div className="settings-section">
              <StatusManagement />
            </div>
          )}

          {activeTab === 'users' && (
            <div className="settings-section">
              <UserManagement />
            </div>
          )}

          {activeTab === 'aircraft-types' && (
            <div className="settings-section">
              <AircraftTypeManagement />
            </div>
          )}

          {activeTab === 'carriers' && (
            <div className="settings-section">
              <CarrierManagement />
            </div>
          )}

          {activeTab === 'cars' && (
            <div className="settings-section">
              <CarManagement />
            </div>
          )}

          {activeTab === 'flights' && (
            <div className="settings-section">
              <FlightManagement />
            </div>
          )}

          {activeTab === 'calibration-tables' && (
            <div className="settings-section">
              <CalibrationTableManagement />
            </div>
          )}

          {activeTab === 'airports' && (
            <div className="settings-section">
              <AirportManagement />
            </div>
          )}

          {activeTab === 'about' && (
            <div className="settings-section">
              <h3>О системе</h3>
              
              <div className="about-info">
                <div className="info-item">
                  <strong>Версия приложения:</strong>
                  <span>1.0.0</span>
                </div>
                
                <div className="info-item">
                  <strong>Дата сборки:</strong>
                  <span>{new Date().toLocaleDateString('ru-RU')}</span>
                </div>
                
                <div className="info-item">
                  <strong>API версия:</strong>
                  <span>v1</span>
                </div>
                
                <div className="info-item">
                  <strong>Поддерживаемые браузеры:</strong>
                  <span>Chrome 90+, Firefox 88+, Safari 14+, Edge 90+</span>
                </div>
              </div>

              <div className="api-documentation">
                <h4>Документация API</h4>
                <p>Система поддерживает следующие API контроллеры:</p>
                <ul>
                  <li><strong>User Controller</strong> - Управление пользователями</li>
                  <li><strong>Car Controller</strong> - Управление машинами</li>
                  <li><strong>Aircraft Type Controller</strong> - Типы самолетов</li>
                  <li><strong>Flight Controller</strong> - Управление рейсами</li>
                  <li><strong>Status Controller</strong> - Статусы</li>
                  <li><strong>Request Controller</strong> - Управление заявками</li>
                  <li><strong>Fuel Controller</strong> - Топливные данные</li>
                  <li><strong>Carrier Controller</strong> - Перевозчики</li>
                  <li><strong>Auth Controller</strong> - Аутентификация</li>
                  <li><strong>Calibration Table Controller</strong> - Тарировочные таблицы</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsPage; 